import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, GripVertical, MessageSquare, Phone, Trash2 } from 'lucide-react';
import {
  describeStepLine,
  stepName,
  type Audience,
  type SequenceStep,
  type WindowDay,
} from '@vagusx/shared';
import type { WorkflowOption, WorkflowPin } from '@vagusx/clients';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { DurationInput } from './DurationInput';
import { Field } from './Field';
import { StackField, WhenToCallField, WorkflowField } from './step-fields';

/**
 * One step, collapsed to a sentence and expanded in place.
 *
 * **Expanding never moves the editing surface to another column.** The previous
 * editor put the step's fields in a pane whose contents depended on an
 * invisible selection, which meant the sequence could only be read by clicking
 * through it a step at a time and holding the rest in your head. Here the
 * collapsed line says what the step does — including the continuation rule,
 * which is the fact authors most often get wrong — so a campaign can be read
 * without opening anything at all.
 *
 * The channel control shows Text as unavailable rather than hiding it. An SMS
 * step resolves on silence (`on_quiet`), which needs conversation state that
 * does not exist yet — enabling it would let someone publish a campaign whose
 * step never resolves, while omitting it entirely reads as an oversight to
 * anyone who knows the schema has two channels.
 */

interface StepCardProps {
  step: SequenceStep;
  index: number;
  isLast: boolean;
  /** Named in the "Starts" row: "1 day after First attempt". */
  previousStepLabel?: string;
  /** Named in the continuation row: "carries on to Last try". */
  nextStepLabel?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (step: SequenceStep) => void;
  /** Absent on the only step in a sequence — a sequence needs at least one. */
  onDelete?: () => void;
  context: StepContext;
}

/** What a step's controls need from the campaign around it. */
export interface StepContext {
  /** Published outbound voice workflows (`workflow-options`); undefined while loading. */
  workflowOptions: WorkflowOption[] | undefined;
  /** Pinned against newest published version, per step id (`GET /:id`). */
  pins: ReadonlyMap<string, WorkflowPin>;
  audienceType: Audience['type'];
  campaignDays: readonly WindowDay[];
  /**
   * The hours calls can go out on a calling day (`callingHours`: the campaign's
   * inside the organization's quiet hours), which a defined step narrows.
   */
  callingHours: { start: string; end: string };
  personaId: string;
  /** VagusX ops only: the stack picker (P4). */
  showStack: boolean;
}

export function StepCard({
  step,
  index,
  isLast,
  previousStepLabel,
  nextStepLabel,
  open,
  onOpenChange,
  onChange,
  onDelete,
  context,
}: StepCardProps): React.ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
  });
  const patch = (over: Partial<SequenceStep>): void => onChange({ ...step, ...over });
  const name = stepName(step, index);
  const bodyId = `step-body-${step.id}`;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'group rounded-lg border bg-background transition-colors',
        open && 'border-primary/40 ring-2 ring-primary/10',
        isDragging && 'z-10 opacity-80 shadow-md'
      )}
    >
      {/* ── The collapsed line, which is also the toggle ──────────────── */}
      <div className="flex items-center gap-1">
        {/*
          The handle owns the drag, not the row: the row is the expand target,
          and making the whole card draggable means every attempt to open a step
          starts a drag instead.
        */}
        <button
          type="button"
          className="cursor-grab touch-none py-3 pl-2 text-muted-foreground/50 active:cursor-grabbing"
          aria-label={`Reorder ${name}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>

        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2.5 py-3 pr-2 text-left"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => onOpenChange(!open)}
        >
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-[11px] font-medium tabular-nums text-background">
            {index + 1}
          </span>
          <span className="shrink-0 text-sm font-medium">{name}</span>
          <span className="truncate text-xs text-muted-foreground">
            {describeStepLine(step, index, isLast)}
          </span>

          {/*
            One dot per attempt this step makes. The campaign's cap is a number
            in a settings row several sections away, and nothing else on the
            screen shows what the sequence actually spends against it.
          */}
          <span className="ml-auto flex shrink-0 items-center gap-1" aria-hidden>
            {Array.from({ length: step.repeat.times }, (_, i) => (
              <span key={i} className="h-1.5 w-1.5 rounded-full bg-primary/60" />
            ))}
          </span>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-180'
            )}
          />
        </button>
      </div>

      {/* ── Everything about the step, in place ───────────────────────── */}
      {open && (
        <div id={bodyId} className="border-t px-3 pb-2">
          <div className="flex items-center gap-3 py-3">
            <Label htmlFor={`step-label-${step.id}`} className="sr-only">
              Step name
            </Label>
            <Input
              id={`step-label-${step.id}`}
              className="h-8 min-w-0 flex-1 text-sm font-medium"
              value={step.label ?? ''}
              maxLength={80}
              placeholder={`Step ${index + 1}`}
              onChange={(e) => patch({ label: e.target.value })}
            />

            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              className="shrink-0"
              value={step.channel}
              onValueChange={(channel) => {
                if (channel === 'voice') patch({ channel, action: { kind: 'call' } });
              }}
            >
              <ToggleGroupItem value="voice" aria-label="Phone call">
                <Phone className="mr-1.5 h-3.5 w-3.5" />
                Call
              </ToggleGroupItem>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* A disabled item swallows hover, so the span carries it. */}
                  <span>
                    <ToggleGroupItem value="sms" aria-label="Text message" disabled>
                      <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
                      Text
                    </ToggleGroupItem>
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-56">
                  Text steps stay open until the patient goes quiet, which needs conversation
                  storage that is not built yet.
                </TooltipContent>
              </Tooltip>
            </ToggleGroup>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              // A sequence with no steps cannot be published, so the last one stays.
              disabled={!onDelete}
              aria-label={`Delete ${name}`}
              onClick={onDelete}
            >
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </div>

          <div className="divide-y border-t">
            <WorkflowField step={step} context={context} onChange={onChange} />

            {/*
              Every step says what starts it. The first is started by the
              audience and has nothing to configure, so it reads as a statement
              rather than a control with one possible value.
            */}
            <Field label="Starts">
              {step.trigger.kind === 'qualifies' ? (
                <span className="text-sm text-muted-foreground">as soon as they are ready</span>
              ) : (
                <>
                  <DurationInput
                    id={`step-trigger-${step.id}`}
                    label="Time after the previous step"
                    minutes={step.trigger.minutes}
                    onChange={(minutes) => patch({ trigger: { kind: 'after_previous', minutes } })}
                  />
                  <span className="truncate text-sm text-muted-foreground">
                    after the last try of {previousStepLabel ?? 'the previous step'}
                  </span>
                </>
              )}
            </Field>

            <Field label="Tries" htmlFor={`step-times-${step.id}`}>
              <Input
                id={`step-times-${step.id}`}
                type="number"
                min={1}
                max={10}
                className="h-9 w-16"
                value={step.repeat.times}
                onChange={(e) => {
                  const times = Number(e.target.value);
                  if (Number.isInteger(times) && times >= 1 && times <= 10) {
                    patch({ repeat: { ...step.repeat, times } });
                  }
                }}
              />
              {/* Spacing only means something with more than one try. */}
              {step.repeat.times > 1 && (
                <>
                  <span className="text-sm text-muted-foreground">spaced</span>
                  <DurationInput
                    id={`step-spacing-${step.id}`}
                    label="Time between tries on this step"
                    minutes={step.repeat.spacingMinutes}
                    onChange={(spacingMinutes) =>
                      patch({ repeat: { ...step.repeat, spacingMinutes } })
                    }
                  />
                  <span className="text-sm text-muted-foreground">apart</span>
                </>
              )}
            </Field>

            <WhenToCallField
              step={step}
              campaignDays={context.campaignDays}
              callingHours={context.callingHours}
              onChange={onChange}
            />

            <Field
              label="A machine answers"
              htmlFor={`step-machine-${step.id}`}
              hint={
                step.onMachine.kind === 'leave_message'
                  ? 'Only on this step\u2019s last try: a message early uses up the one chance it gives you.'
                  : 'No message. The try counts as not reached.'
              }
            >
              <Switch
                id={`step-machine-${step.id}`}
                checked={step.onMachine.kind === 'leave_message'}
                onCheckedChange={(on) =>
                  patch({
                    onMachine: on
                      ? { kind: 'leave_message', slot: 'default' }
                      : { kind: 'hang_up' },
                  })
                }
              />
              <span className="text-sm">Leave a message on the last try</span>
            </Field>

            <Field
              label="Never reaches them"
              htmlFor={`step-after-${step.id}`}
              hint="Every try ended without talking to them: no answer, voicemail, or busy."
            >
              {isLast ? (
                <span className="text-sm text-muted-foreground">
                  The run ends and the referral goes back to the front desk.
                </span>
              ) : (
                <Select
                  value={step.afterLastTry}
                  onValueChange={(afterLastTry) =>
                    (afterLastTry === 'next' || afterLastTry === 'stop') && patch({ afterLastTry })
                  }
                >
                  <SelectTrigger id={`step-after-${step.id}`} className="w-72">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="next">
                      Go on to {nextStepLabel ?? 'the next step'}
                    </SelectItem>
                    <SelectItem value="stop">Stop and hand it to the front desk</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field
              label="Requested callbacks"
              htmlFor={`step-callback-${step.id}`}
              hint="Their time replaces this step's own timing."
            >
              <Switch
                id={`step-callback-${step.id}`}
                checked={step.onCallbackRequested === 'use_requested_time'}
                onCheckedChange={(on) =>
                  patch({ onCallbackRequested: on ? 'use_requested_time' : 'use_standard_wait' })
                }
              />
              <span className="text-sm">Honour a requested time</span>
            </Field>

            {context.showStack && (
              <StackField step={step} personaId={context.personaId} onChange={onChange} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
